import { Injectable, inject } from '@angular/core';
import { MembershipProduct } from '@app/core/models';
import { ViewService } from '@makiwin/ngx-drupal8-rest';
import { TranslateService } from '@ngx-translate/core';
import { Observable, map, mergeMap, of } from 'rxjs';
import { AuthSessionService } from '../auth-session/auth-session.service';
import { RolesService } from '../roles/roles.service';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionPlansService {
  /**
   * Product ids for membership plans.
   */
  readonly productIds = {
    kiter: '2',
    instructor: '3',
    instructorL2: '3444',
    instructorL3: '3445',
    assistant: '22',
    center: '21',
  };
  protected membershipPlans?: MembershipProduct[];
  /**
   * Cached for membership plans.
   */
  protected cachedPlans: {
    [key: string]: MembershipProduct[];
  } = {};

  private authSessionService: AuthSessionService = inject(AuthSessionService);
  private rolesService: RolesService = inject(RolesService);

  constructor(private viewService: ViewService, private translate: TranslateService) {
    this.translate.onLangChange.subscribe(() => {
      if (this.authSessionService.isLogged) {
        this.delete();
        this.get().subscribe();
      }
    });
  }

  get isEmptyMembershipPlans(): boolean {
    if (!this.membershipPlans) {
      return false;
    }

    return this.membershipPlans.length === 0;
  }

  /**
   * Get membership plans, in case if there's no membership plans,
   * it will check if the user has an active subscription, or from expired subscriptions.
   *
   * @returns
   *  Obsavable of membership plans.
   */
  get(): Observable<MembershipProduct[]> {
    if (this.membershipPlans) {
      return of(this.membershipPlans);
    }
    const path = `/${this.translate.currentLang.replace('en', '')}/api/v1/membership_products.json`.replace('//', '/');
    return this.viewService.get(path).pipe(
      mergeMap((data: MembershipProduct[]) => {
        if (
          !this.authSessionService.currentUserProfile?.top_qualification?.toLowerCase().includes('examiner') &&
          data.length
        ) {
          this.membershipPlans = this.formatMembershipPlans(data);
          return of(this.membershipPlans);
        }
        const endpoint =
          (this.rolesService.isExpired
            ? '/api/v1/user-ended-subscription.json'
            : '/api/v1/user-active-subscription.json') + `/${this.authSessionService.currentUserProfile?.uid}/`;
        return this.viewService.get(endpoint).pipe(
          mergeMap((memberhsip) => {
            if (!memberhsip?.length) {
              return of([]);
            }
            return this.getByProductId(memberhsip[0].product_id).pipe(map((plans) => (this.membershipPlans = plans)));
          })
        );
      })
    );
  }

  /**
   * Get membership plans by product id.
   * @param productId
   * Product id fo the membership plan.
   * @returns
   * An observable of membership plans.
   */
  getByProductId(productId: number): Observable<MembershipProduct[]> {
    const currentLang = this.translate.currentLang.replace('en', '');
    const key = `${productId}-${currentLang}`;
    if (this.cachedPlans[key]) {
      return of(this.cachedPlans[key]);
    }

    const path = `/${currentLang}/api/v1/membership_plan_by_product.json`.replace('//', '/');
    return this.viewService.get(path, { filters: { product_id: productId } }).pipe(
      map((plans) => {
        this.cachedPlans[key] = this.formatMembershipPlans(plans);
        return this.cachedPlans[key];
      })
    );
  }

  /**
   * Fetch the promotion date, if present.
   * @returns
   * Observable of the promotion date.
   */
  getPromotionDate(): Observable<any> {
    return this.viewService.get('/api/membership_promotion_date');
  }

  /**
   * Delete the cached membership plans.
   */
  delete() {
    delete this.membershipPlans;
    this.cachedPlans = {};
  }

  /**
   * Format and prepare for display the membership plans.
   *
   * @param plans
   * Plans to format.
   * @returns
   * Array of memberhsip plans formatted.
   */
  private formatMembershipPlans(plans: MembershipProduct[]): MembershipProduct[] {
    // if feature details is not equal for all plans - not the title
    plans = plans.map((plan) => {
      // remove L1, L2, L3 from title
      plan.product_title = plan.product_title?.replace(/ l([1-3])/gim, '');
      plan.field_features_export = plan.field_features_export.filter((f) => f.title);
      // plan.field_features_export = plan.field_features_export.map((feature) => {
      //   const newDiv = document.createElement('div');
      //   newDiv.innerHTML = feature.details;

      //   feature.title = newDiv.children[0].outerHTML;
      //   if (newDiv.children[1]) {
      //     feature.description = newDiv.children[1].outerHTML;
      //   }
      //   return feature;
      // });
      return plan;
    });

    return plans;
  }
}
